import React, { createContext, useCallback, useContext } from "react";

import { Variant } from "@amplitude/experiment-node-server";

import { FlagKey } from "types/experiments";

export type ExperimentsHashmap = Record<string, Variant>;
interface ExperimentsContextType {
  experiments: ExperimentsHashmap;
  getExperiment: (experimentName: FlagKey) => Variant | null;
}

const ExperimentsContext = createContext<ExperimentsContextType | undefined>(
  undefined,
);

export const ExperimentsContextProvider = ({
  children,
  experiments,
}: {
  children: React.ReactNode;
  experiments: ExperimentsHashmap;
}) => {
  const getExperiment = useCallback(
    (experimentName: FlagKey) => {
      return experiments?.[experimentName] ?? null;
    },
    [experiments],
  );

  return (
    <ExperimentsContext.Provider value={{ experiments, getExperiment }}>
      {children}
    </ExperimentsContext.Provider>
  );
};

export const withExperimentsContext = WrappedComponent => {
  const WithExperiments = props => {
    return (
      <ExperimentsContextProvider
        experiments={
          (props?.pageProps?.experiments ?? {}) as ExperimentsHashmap
        }
      >
        <WrappedComponent {...props} />
      </ExperimentsContextProvider>
    );
  };

  return WithExperiments;
};

export const useExperiments = () => {
  const context = useContext(ExperimentsContext);

  if (context === undefined) {
    throw new Error(
      "useExperiments must be used within a ExperimentsContextProvider",
    );
  }

  return context;
};
